/**
 * Callback Scheduler Service
 * Manages scheduled callbacks for debt collection, follow-ups, and reminders
 * 
 * Key Features:
 * - Schedule callbacks at specific times
 * - Automatic retry on failed calls
 * - Integration with AgentLabs campaign executor
 * - Agent can schedule follow-ups during calls
 */

import cron from 'node-cron';
import { v4 as uuidv4 } from 'uuid';
import type {
  ScheduledCallback,
  CallbackLog,
  CallbackReason,
  CallbackReasonDetails,
  CallbackStatus,
  CallResult,
  ExecutionContext,
} from '../types';

export class CallbackSchedulerService {
  private db: any;
  private campaignExecutor: any;
  private cronJob: cron.ScheduledTask | null = null;

  constructor(db: any, campaignExecutor?: any) {
    this.db = db;
    this.campaignExecutor = campaignExecutor;
  }

  /**
   * Start the callback scheduler (runs every minute)
   */
  start(): void {
    if (this.cronJob) {
      console.log('[Callback Scheduler] Already running');
      return;
    }

    this.cronJob = cron.schedule('* * * * *', async () => {
      await this.processScheduledCallbacks();
    });

    console.log('[Callback Scheduler] Started - checking every minute');
  }

  /**
   * Stop the callback scheduler
   */
  stop(): void {
    if (this.cronJob) {
      this.cronJob.stop();
      this.cronJob = null;
      console.log('[Callback Scheduler] Stopped');
    }
  }

  /**
   * Schedule a new callback
   */
  async scheduleCallback(
    userId: string,
    agentId: string,
    data: {
      phoneNumber: string;
      scheduledAt: Date;
      reason: CallbackReason;
      reasonDetails?: CallbackReasonDetails;
      customerName?: string;
      customerId?: string;
      agentContext?: string;
      dataQueryId?: string;
      maxRetries?: number;
      retryIntervalMinutes?: number;
      createdBy?: 'manual' | 'agent' | 'api' | 'campaign';
      originalCallId?: string;
    }
  ): Promise<ScheduledCallback> {
    const callback: ScheduledCallback = {
      id: uuidv4(),
      userId,
      agentId,
      phoneNumber: data.phoneNumber,
      scheduledAt: data.scheduledAt,
      timezone: 'Asia/Riyadh', // Default timezone
      reason: data.reason,
      reasonDetails: data.reasonDetails,
      customerName: data.customerName,
      customerId: data.customerId,
      agentContext: data.agentContext,
      dataQueryId: data.dataQueryId,
      status: 'pending',
      retryCount: 0,
      maxRetries: data.maxRetries ?? 3,
      retryIntervalMinutes: data.retryIntervalMinutes ?? 60,
      followUpScheduled: false,
      createdBy: data.createdBy ?? 'manual',
      originalCallId: data.originalCallId,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    // Insert into database
    await this.insertCallback(callback);

    console.log(`[Callback Scheduler] Scheduled callback ${callback.id} for ${data.phoneNumber} at ${data.scheduledAt}`);

    return callback;
  }

  /**
   * Schedule a callback from agent tool call (during conversation)
   * Parses relative time like "in 2 days", "tomorrow at 10am", etc.
   */
  async scheduleCallbackFromAgent(
    context: ExecutionContext,
    agentId: string,
    phoneNumber: string,
    scheduledAtString: string,
    reason: CallbackReason,
    reasonDetails?: CallbackReasonDetails,
    customerName?: string,
    agentContext?: string
  ): Promise<{ success: boolean; message: string; scheduledAt?: Date; callbackId?: string }> {
    try {
      const scheduledAt = this.parseScheduledTime(scheduledAtString);

      if (!scheduledAt || scheduledAt <= new Date()) {
        return {
          success: false,
          message: 'Invalid or past scheduled time. Please specify a future time.',
        };
      }

      const callback = await this.scheduleCallback(context.userId, agentId, {
        phoneNumber,
        scheduledAt,
        reason,
        reasonDetails,
        customerName,
        customerId: context.customerId,
        agentContext,
        createdBy: 'agent',
        originalCallId: context.callId,
      });

      return {
        success: true,
        message: `تم جدولة المكالمة بنجاح في ${this.formatDateArabic(scheduledAt)}`,
        scheduledAt,
        callbackId: callback.id,
      };
    } catch (error: any) {
      console.error('[Callback Scheduler] Error scheduling from agent:', error);
      return {
        success: false,
        message: 'حدث خطأ في جدولة المكالمة. يرجى المحاولة مرة أخرى.',
      };
    }
  }

  /**
   * Process all pending callbacks that are due
   */
  private async processScheduledCallbacks(): Promise<void> {
    const now = new Date();

    // Get all pending callbacks that are due
    const pendingCallbacks = await this.getPendingCallbacks(now);

    for (const callback of pendingCallbacks) {
      try {
        await this.executeCallback(callback);
      } catch (error) {
        console.error(`[Callback Scheduler] Error executing callback ${callback.id}:`, error);
      }
    }
  }

  /**
   * Execute a single callback
   */
  private async executeCallback(callback: ScheduledCallback): Promise<void> {
    console.log(`[Callback Scheduler] Executing callback ${callback.id} to ${callback.phoneNumber}`);

    // Update status to in_progress
    await this.updateCallbackStatus(callback.id, 'in_progress');

    try {
      // Prepare agent context with callback reason
      const enhancedContext = this.buildAgentContext(callback);

      // Execute the call via campaign executor or direct API
      const callResult = await this.initiateCall(callback, enhancedContext);

      // Log the attempt
      await this.logCallbackAttempt(callback.id, {
        attemptNumber: callback.retryCount + 1,
        status: callResult.status,
        callId: callResult.callId,
        durationSeconds: callResult.durationSeconds,
      });

      // Handle result
      if (callResult.status === 'answered') {
        await this.updateCallbackStatus(callback.id, 'completed', {
          callId: callResult.callId,
          callResult: callResult.status,
          callSummary: callResult.summary,
        });
      } else {
        // Handle retry logic
        await this.handleCallbackRetry(callback, callResult.status);
      }
    } catch (error: any) {
      console.error(`[Callback Scheduler] Call failed for ${callback.id}:`, error);

      await this.logCallbackAttempt(callback.id, {
        attemptNumber: callback.retryCount + 1,
        status: 'failed',
        errorMessage: error.message,
      });

      await this.handleCallbackRetry(callback, 'failed');
    }
  }

  /**
   * Build agent context for the callback
   */
  private buildAgentContext(callback: ScheduledCallback): string {
    let context = callback.agentContext || '';

    // Add reason-specific context
    switch (callback.reason) {
      case 'payment_reminder':
        context += `\n\n[معلومات المكالمة]
هذه مكالمة تذكير بالدفع.
اسم العميل: ${callback.customerName || 'غير معروف'}
${callback.reasonDetails?.amount ? `المبلغ المستحق: ${callback.reasonDetails.amount} ${callback.reasonDetails.currency || 'ريال'}` : ''}
${callback.reasonDetails?.invoiceId ? `رقم الفاتورة: ${callback.reasonDetails.invoiceId}` : ''}
${callback.reasonDetails?.dueDate ? `تاريخ الاستحقاق: ${callback.reasonDetails.dueDate}` : ''}

الهدف: تذكير العميل بالمبلغ المستحق ومحاولة الحصول على وعد بالدفع أو جدولة موعد للمتابعة.
إذا طلب العميل معاودة الاتصال لاحقاً، استخدم أداة schedule_callback لجدولة مكالمة متابعة.`;
        break;

      case 'follow_up':
        context += `\n\n[معلومات المكالمة]
هذه مكالمة متابعة.
اسم العميل: ${callback.customerName || 'غير معروف'}
${callback.agentContext || 'تابع مع العميل بخصوص المحادثة السابقة.'}`;
        break;

      case 'appointment_confirmation':
        context += `\n\n[معلومات المكالمة]
هذه مكالمة تأكيد موعد.
اسم العميل: ${callback.customerName || 'غير معروف'}
${callback.reasonDetails?.appointmentDate ? `تاريخ الموعد: ${callback.reasonDetails.appointmentDate}` : ''}
${callback.reasonDetails?.appointmentTime ? `وقت الموعد: ${callback.reasonDetails.appointmentTime}` : ''}

الهدف: تأكيد حضور العميل للموعد أو إعادة جدولته إذا لزم الأمر.`;
        break;
    }

    return context;
  }

  /**
   * Handle retry logic for failed calls
   */
  private async handleCallbackRetry(callback: ScheduledCallback, result: CallResult | 'failed'): Promise<void> {
    const newRetryCount = callback.retryCount + 1;

    if (newRetryCount >= callback.maxRetries) {
      // Max retries reached
      await this.updateCallbackStatus(callback.id, 'failed', {
        callResult: result,
      });
      console.log(`[Callback Scheduler] Callback ${callback.id} failed after ${newRetryCount} attempts`);
      return;
    }

    // Schedule next retry
    const nextRetryAt = new Date(Date.now() + callback.retryIntervalMinutes * 60 * 1000);

    await this.updateCallbackForRetry(callback.id, newRetryCount, nextRetryAt);

    console.log(`[Callback Scheduler] Callback ${callback.id} scheduled for retry at ${nextRetryAt}`);
  }

  /**
   * Initiate the actual call
   */
  private async initiateCall(
    callback: ScheduledCallback,
    enhancedContext: string
  ): Promise<{
    status: CallResult;
    callId?: string;
    durationSeconds?: number;
    summary?: string;
  }> {
    // This would integrate with the AgentLabs call system
    // For now, return a mock result
    console.log(`[Callback Scheduler] Would initiate call to ${callback.phoneNumber} with agent ${callback.agentId}`);

    // TODO: Integrate with actual call system
    // const result = await this.campaignExecutor.executeCall({
    //   agentId: callback.agentId,
    //   phoneNumber: callback.phoneNumber,
    //   context: enhancedContext,
    // });

    return {
      status: 'answered', // Mock
      callId: uuidv4(),
      durationSeconds: 120,
      summary: 'Mock call completed',
    };
  }

  /**
   * Parse relative time strings like "in 2 days", "tomorrow at 10am"
   */
  private parseScheduledTime(timeString: string): Date | null {
    const now = new Date();
    const lowerStr = timeString.toLowerCase().trim();

    // Handle "in X days/hours/minutes"
    const inMatch = lowerStr.match(/in\s+(\d+)\s+(day|hour|minute|min|hr)s?/i);
    if (inMatch) {
      const amount = parseInt(inMatch[1]);
      const unit = inMatch[2].toLowerCase();
      
      if (unit.startsWith('day')) {
        return new Date(now.getTime() + amount * 24 * 60 * 60 * 1000);
      } else if (unit.startsWith('hour') || unit === 'hr') {
        return new Date(now.getTime() + amount * 60 * 60 * 1000);
      } else if (unit.startsWith('min')) {
        return new Date(now.getTime() + amount * 60 * 1000);
      }
    }

    // Handle Arabic: "بعد يومين", "بعد ساعتين"
    const arabicMatch = lowerStr.match(/بعد\s+(\d+|يوم|ساعة|دقيقة)/);
    if (arabicMatch) {
      if (lowerStr.includes('يوم')) {
        const days = lowerStr.includes('يومين') ? 2 : 1;
        return new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
      }
      if (lowerStr.includes('ساعة') || lowerStr.includes('ساعتين')) {
        const hours = lowerStr.includes('ساعتين') ? 2 : 1;
        return new Date(now.getTime() + hours * 60 * 60 * 1000);
      }
    }

    // Handle "tomorrow"
    if (lowerStr.includes('tomorrow') || lowerStr.includes('غدا') || lowerStr.includes('بكره')) {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(10, 0, 0, 0); // Default to 10 AM
      return tomorrow;
    }

    // Try parsing as ISO date
    const parsed = new Date(timeString);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }

    return null;
  }

  /**
   * Format date in Arabic
   */
  private formatDateArabic(date: Date): string {
    return date.toLocaleDateString('ar-SA', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // Database helper methods (to be implemented with actual DB queries)
  private async insertCallback(callback: ScheduledCallback): Promise<void> {
    // Insert callback into db_scheduled_callbacks table
    console.log('[Callback Scheduler] Insert callback:', callback.id);
  }

  private async getPendingCallbacks(beforeTime: Date): Promise<ScheduledCallback[]> {
    // Query db_scheduled_callbacks where status = 'pending' and scheduled_at <= beforeTime
    console.log('[Callback Scheduler] Getting pending callbacks before:', beforeTime);
    return [];
  }

  private async updateCallbackStatus(
    callbackId: string,
    status: CallbackStatus,
    extra?: Partial<ScheduledCallback>
  ): Promise<void> {
    console.log(`[Callback Scheduler] Update callback ${callbackId} status to ${status}`);
  }

  private async updateCallbackForRetry(
    callbackId: string,
    retryCount: number,
    nextRetryAt: Date
  ): Promise<void> {
    console.log(`[Callback Scheduler] Update callback ${callbackId} for retry #${retryCount}`);
  }

  private async logCallbackAttempt(
    callbackId: string,
    log: Partial<CallbackLog>
  ): Promise<void> {
    console.log(`[Callback Scheduler] Log attempt for callback ${callbackId}`);
  }

  /**
   * Get callbacks for a user (for UI display)
   */
  async getCallbacksForUser(
    userId: string,
    filters?: {
      status?: CallbackStatus;
      fromDate?: Date;
      toDate?: Date;
      limit?: number;
    }
  ): Promise<ScheduledCallback[]> {
    // Query with filters
    console.log('[Callback Scheduler] Getting callbacks for user:', userId);
    return [];
  }

  /**
   * Cancel a scheduled callback
   */
  async cancelCallback(callbackId: string, userId: string): Promise<boolean> {
    console.log(`[Callback Scheduler] Cancelling callback ${callbackId}`);
    return true;
  }

  /**
   * Reschedule a callback
   */
  async rescheduleCallback(
    callbackId: string,
    userId: string,
    newScheduledAt: Date
  ): Promise<boolean> {
    console.log(`[Callback Scheduler] Rescheduling callback ${callbackId} to ${newScheduledAt}`);
    return true;
  }
}
